import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
export interface CreateIncomeData {
  booking_id: string;
  income_type:
    | "Hospedaje"
    | "Early Checkin"
    | "Late Checkout"
    | "Limpieza adicional"
    | "Servicios"
    | "Otro";
  payment_type: "Transferencia" | "Efectivo";
  gross_payment: number;
  iva?: number;
  ret_iva?: number;
  ret_isr?: number;
  comission?: number;
  payout: number;
  payout_date: string;
  fiscal_invoice: boolean;
}

export const useCreateIncome = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateIncomeData) => {
      const { data: income, error } = await supabase
        .from("property_income")
        .insert(data)
        .select()
        .single();

      if (error) throw error;

      if (data.income_type === "Hospedaje") {
        const { error: bookingError } = await supabase
          .from("bookings")
          .update({ paid: true })
          .eq("booking_id", data.booking_id);

        if (bookingError) {
          console.error("Error al actualizar el estado de pago:", bookingError);
        }
      }

      return income;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["income"] });
      queryClient.invalidateQueries({ queryKey: ["income-by-booking"] });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["booking"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-aggregates"] });
      toast({
        title: "Éxito",
        description: "Ingreso creado correctamente",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Error al crear el ingreso",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateIncome = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateIncomeData>;
    }) => {
      const { data: income, error } = await supabase
        .from("property_income")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return income;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["income"] });
      queryClient.invalidateQueries({ queryKey: ["income-by-booking"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-aggregates"] });
      toast({
        title: "Éxito",
        description: "Ingreso actualizado correctamente",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Error al actualizar el ingreso",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteIncome = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("property_income")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["income"] });
      queryClient.invalidateQueries({ queryKey: ["income-by-booking"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-aggregates"] });
      toast({
        title: "Éxito",
        description: "Ingreso eliminado correctamente",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Error al eliminar el ingreso",
        variant: "destructive",
      });
    },
  });
};
